import {
  BaseTransaction,
  Response,
  TransactionAML,
  TransactionWarning,
  TransactionWarningActionBody,
  apiAbortController,
} from '@ui/src';
import {
  CheckTransaction,
  CheckTransactionResult,
} from '../../../types/check-transaction';
import api from '../../../api/api';
import { CofProps, StatusUpdate, WarningsInfo } from '../../transactions/type';
import {
  CreateInternalTransactionFormValues,
  UpdateInternalTransaction,
} from '../types';

export const internalTransactionsListApi = api.injectEndpoints({
  endpoints: (build) => ({
    getInternalList: build.query<Response<BaseTransaction>, string>({
      query: (query) => ({
        url: `/v1/transactions-internal${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getInternalListStatuses: build.query<Response<StatusUpdate>, string>({
      query: (query) => ({
        url: `/v1/transactions-internal/statuses${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getInternalTransactionById: build.query<{ data: BaseTransaction }, string>({
      query: (id) => ({
        url: `/v1/transactions-internal/${id}?include=beneficiary&include=contract`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getInternalAml: build.query<Response<TransactionAML>, string>({
      query: (id) => ({
        url: `/v1/scores-results?transactionId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getInternalTransactionWarnings: build.query<
      Response<TransactionWarning>,
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-internal/${id}/warnings?take=100`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getInternalTransactionWarning: build.query<
      { data: TransactionWarning },
      { id: string; warningId: string }
    >({
      query: (data) => ({
        url: `/v1/transactions-internal/${data.id}/warnings/${data.warningId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    applyInternalTransactionWarningAction: build.mutation<
      { data: TransactionWarning },
      {
        body: TransactionWarningActionBody;
        transactionId: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/transactions-internal/${data.transactionId}/warnings/${data.warningId}/action`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    checkInternalTransaction: build.mutation<
      { data: CheckTransactionResult },
      {
        body: CheckTransaction;
        abortController: {
          abortPendingRequest: () => void;
          getSignalToken: () => AbortSignal;
        };
      }
    >({
      query: ({ body, abortController }) => ({
        url: `/v1/transactions-internal/check`,
        method: 'POST',
        body,
        signal: abortController.getSignalToken(),
      }),
    }),
    getSenderClientsOfficias: build.query<Response<CofProps>, string>({
      query: (id) => ({
        url: `/v1/clients-officials?organizationId=${id}&status=APPROVED&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getRecipientClientsOfficias: build.query<Response<CofProps>, string>({
      query: (id) => ({
        url: `/v1/clients-officials?organizationId=${id}&status=APPROVED&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportInternalTransaction: build.mutation<
      any,
      Record<string, string[] | string | number>
    >({
      query: (body) => ({
        url: '/v1/transactions-internal/export',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createInternalTransaction: build.mutation<
      { data: BaseTransaction },
      CreateInternalTransactionFormValues
    >({
      query: (body) => ({
        url: '/v1/transactions-internal',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateInternalTransaction: build.mutation<
      { data: BaseTransaction },
      { body: UpdateInternalTransaction; id: string }
    >({
      query: (data) => ({
        url: `/v1/transactions-internal/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    resetInternalTransactionWarningAction: build.mutation<
      { data: TransactionWarning },
      {
        transactionId: string;
        warningId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/transactions-internal/${data.transactionId}/warnings/${data.warningId}/reset`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getInternalTransactionWarningsInfo: build.query<
      { data: WarningsInfo },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-internal/${id}/warnings/info`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetInternalListQuery,
  useLazyGetInternalListQuery,
  useLazyGetInternalAmlQuery,
  useLazyGetInternalTransactionByIdQuery,
  useLazyGetInternalTransactionWarningsQuery,
  useApplyInternalTransactionWarningActionMutation,
  useCheckInternalTransactionMutation,
  useLazyGetInternalListStatusesQuery,
  useLazyGetRecipientClientsOfficiasQuery,
  useLazyGetSenderClientsOfficiasQuery,
  useExportInternalTransactionMutation,
  useCreateInternalTransactionMutation,
  useUpdateInternalTransactionMutation,
  useResetInternalTransactionWarningActionMutation,
  useLazyGetInternalTransactionWarningQuery,
  useLazyGetInternalTransactionWarningsInfoQuery,
} = internalTransactionsListApi;

export const {
  endpoints: {
    getInternalList,
    getInternalTransactionById,
    getInternalAml,
    getInternalTransactionWarnings,
    applyInternalTransactionWarningAction,
    checkInternalTransaction,
    getInternalListStatuses,
    getSenderClientsOfficias,
    getRecipientClientsOfficias,
    exportInternalTransaction,
    createInternalTransaction,
    updateInternalTransaction,
    resetInternalTransactionWarningAction,
    getInternalTransactionWarning,
    getInternalTransactionWarningsInfo,
  },
} = internalTransactionsListApi;
